import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { RiverMarketsRaw } from './types'

/**
 * Synthesized per-TroveManager lender key, e.g. `RIVER_8453_2` (= Base cbBTC
 * market). The CHAIN ID is part of the key (Fluid convention) so keys stay
 * GLOBALLY unique — River deploys the same factory indexes on BNB, Base and
 * Hemi with different collaterals.
 */
export function riverLenderKey(
  lender: string,
  chainId: string | number,
  index: number,
): string {
  return `${lender}_${chainId}_${index}`
}

/** Recover `{ lender, chainId, index }` from a per-market key (or undefined). */
export function riverKeyParts(
  key: string,
): { lender: string; chainId: string; index: number } | undefined {
  if (!key.startsWith('RIVER_')) return undefined
  const suffix = key.slice('RIVER_'.length)
  const m = suffix.match(/^(\d+)_(\d+)$/)
  if (!m) return undefined
  return { lender: 'RIVER', chainId: m[1], index: Number(m[2]) }
}

const WAD = 1e18

function toHuman(raw: bigint, decimals: number): number {
  return Number(raw) / 10 ** decimals
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name: symbol, decimals }
}

/**
 * Map one River deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed by `RIVER_<chainId>_<TM_INDEX>` — one key
 * per TroveManager (collateral market).
 *
 * Per market:
 *  - COLLATERAL entry: totals = market collateral; LTV = 1/MCR; the
 *    "liquidation penalty" is approximated as MCR − 1 (V1-style liquidation
 *    hands the SP the WHOLE trove collateral, so the borrower's max loss at
 *    liquidation ≈ the ICR buffer — there is no fixed penalty parameter);
 *  - LOAN entry (satUSD): `totalDebt` = market debt; the interest rate is
 *    PROTOCOL-SET (currently 0% → `variableBorrowRate` 0); the one-off
 *    decaying-baseRate MINT FEE is NOT an APR and therefore lives in
 *    `params.market.river.mintFeeRate` (WAD string), not in the rate fields;
 *    `borrowLiquidity` = min(coll·price/MCR − debt, maxSystemDebt − debt).
 *  - The single per-chain StabilityPool ("earn") is attached to market
 *    index 0 ONLY (its loan-row `totalDeposits`); other markets report 0 so
 *    chain aggregates do not double-count. SP yield is 0 while the protocol
 *    rate is 0% (SP earns liquidation gains + OSHI emissions only).
 */
export function convertRiverMarketsToResponse(
  raw: RiverMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  const chainData = raw?.chainData
  if (!cfg || !chainData) return out

  const debtAddr = cfg.debtToken.toLowerCase()
  const debtDecimals = cfg.debtDecimals ?? 18
  const debtToken = tokens[debtAddr]
  const debtSymbol = debtToken?.symbol ?? 'satUSD'
  const debtPriceKey =
    toOracleKey(debtToken?.assetGroup!) || toGenericPriceKey(debtAddr, chainId)
  // Redeemable at par — peg to 1 USD unless the shared price map knows better.
  const debtPrice = prices[debtPriceKey] || 1
  const spDeposits =
    raw.spDeposits !== null ? toHuman(raw.spDeposits, debtDecimals) : 0

  for (const m of raw.markets ?? []) {
    const market = m.market
    const lenderKey = riverLenderKey(raw.lender, chainId, market.index)
    const collAddr = market.collToken.toLowerCase()
    const collDecimals = market.collDecimals
    const collToken = tokens[collAddr]
    const collSymbol =
      collToken?.symbol ?? market.name?.split(' / ').pop() ?? 'COLL'
    const marketName = market.name ?? `${debtSymbol} / ${collSymbol}`

    const collPriceKey =
      toOracleKey(collToken?.assetGroup!) ||
      toGenericPriceKey(collAddr, chainId)
    const collPrice =
      (m.price !== null ? Number(m.price) / WAD : 0) ||
      (prices[collPriceKey] ?? 0)

    const entireDebt =
      m.entireDebt !== null ? toHuman(m.entireDebt, debtDecimals) : 0
    const entireColl =
      m.entireColl !== null ? toHuman(m.entireColl, collDecimals) : 0
    const halted = !!market.paused || !!market.sunsetting

    const mcr = Number(market.mcr) / WAD || 1.1
    const ltv = 1 / mcr
    // V1 liquidation: the SP absorbs the whole trove — borrower loss at
    // liquidation ≈ the ICR buffer above par. No protocol penalty param.
    const liqPenalty = Math.max(mcr - 1, 0)

    // Mintable headroom: collateral capacity at MCR, capped by the market's
    // owner-set debt ceiling.
    let borrowLiquidity = Math.max(
      0,
      (entireColl * collPrice) / mcr / debtPrice - entireDebt,
    )
    if (market.maxSystemDebt) {
      const cap = Number(market.maxSystemDebt) / 10 ** debtDecimals
      borrowLiquidity = Math.min(borrowLiquidity, Math.max(0, cap - entireDebt))
    }
    if (halted) borrowLiquidity = 0

    // Single per-chain SP → attach to market 0 only (no double counting).
    const spHere = market.index === 0 ? spDeposits : 0

    // @ts-ignore — built incrementally, matches Midnight/Term/Liquity converters
    const entry: MorphoGeneralPublicResponse = { data: {} }

    // 1. COLLATERAL entry — deposit-only.
    const collUid = createMarketUid(chainId, lenderKey, collAddr)
    entry.data[collUid] = {
      marketUid: collUid,
      name: 'Collateral ' + collSymbol,
      poolId: market.troveManager.toLowerCase(),
      underlying: collAddr,
      asset: currencyFor(collAddr, collDecimals, collSymbol, tokens),
      totalDeposits: entireColl,
      totalDebtStable: 0,
      totalDebt: 0,
      totalLiquidity: entireColl,
      borrowLiquidity: 0,
      totalLiquidityUSD: entireColl * collPrice,
      borrowLiquidityUSD: 0,
      totalDepositsUSD: entireColl * collPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: 0,
      utilization: 0,
      depositRate: 0,
      variableBorrowRate: 0,
      stableBorrowRate: 0,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: collDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: ltv,
          collateralFactor: ltv,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          collateralDisabled: false,
          debtDisabled: true,
        },
      },
      closeFactor: 1,
      collateralActive: true,
      borrowingEnabled: false,
      depositsEnabled: !halted,
      hasStable: false,
      isActive: !halted,
      isFrozen: halted,
    } as any

    // 2. LOAN entry — satUSD debt; SP earn side only on index 0.
    const loanUid = createMarketUid(chainId, lenderKey, debtAddr)
    entry.data[loanUid] = {
      marketUid: loanUid,
      name: debtSymbol,
      poolId: cfg.xapp.toLowerCase(),
      underlying: debtAddr,
      asset: currencyFor(debtAddr, debtDecimals, debtSymbol, tokens),
      totalDeposits: spHere,
      totalDebtStable: 0,
      totalDebt: entireDebt,
      totalLiquidity: borrowLiquidity,
      borrowLiquidity,
      totalLiquidityUSD: borrowLiquidity * debtPrice,
      borrowLiquidityUSD: borrowLiquidity * debtPrice,
      totalDepositsUSD: spHere * debtPrice,
      totalDebtStableUSD: 0,
      totalDebtUSD: entireDebt * debtPrice,
      utilization: 0,
      // No interest yield while the protocol rate is 0% — SP earns
      // liquidation gains + OSHI emissions only (not modeled here).
      depositRate: 0,
      // Ongoing annual borrow rate as a PERCENT (WAD → %). Live on-chain
      // `interestRate()` preferred, metadata snapshot as fallback. River
      // (Satoshi, Prisma lineage) runs at 0% everywhere today — the real
      // borrow cost is the one-off decaying MINT FEE, surfaced separately as
      // `params.market.river.mintFeeRate` (it is not an APR, so it never
      // belongs in this field).
      variableBorrowRate:
        (Number(m.interestRate ?? market.interestRate ?? 0) / WAD) * 100,
      stableBorrowRate: 0,
      // River (Satoshi) has NO ongoing interest — the borrow cost is the
      // one-off decaying mint fee (`getBorrowingRateWithDecay`, WAD → percent).
      rateModel: 'zeroInterest',
      originationFee:
        m.mintFeeRate !== null
          ? (Number(m.mintFeeRate) / WAD) * 100
          : undefined,
      intrinsicYield: 0,
      rewards: undefined,
      decimals: debtDecimals,
      config: {
        0: {
          category: 0,
          borrowCollateralFactor: 0,
          collateralFactor: 0,
          borrowFactor: 1,
          liquidationPenalty: liqPenalty,
          closeFactor: 1,
          // SP deposits earn but never collateralize.
          collateralDisabled: true,
          debtDisabled: halted,
        },
      },
      closeFactor: 1,
      collateralActive: false,
      borrowingEnabled: !halted,
      depositsEnabled: !halted,
      hasStable: false,
      variableBorrowDisabled: halted,
      isActive: !halted,
      isFrozen: halted,
    } as any

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName,
        loanDecimals: debtDecimals,
        collateralDecimals: collDecimals,
        // TroveManager doubles as the market id (marketUid address).
        id: market.troveManager.toLowerCase(),
        lltv: market.mcr,
        oracle: zeroAddress,
        irm: zeroAddress,
        collateralAddress: collAddr,
        loanAddress: debtAddr,
        // --- River descriptor (verbatim metadata + live snapshot; consumed
        // by calldata builders + worker-api resolvers + yield-tracer) ---
        river: {
          index: market.index,
          minNetDebt: chainData.minNetDebt,
          debtGasCompensation: market.debtGasCompensation,
          mintFeeFloor: market.borrowingFeeFloor,
          mintFeeCap: market.maxBorrowingFee,
          /** Current decayed one-off mint-fee rate (WAD string). */
          mintFeeRate:
            m.mintFeeRate !== null ? m.mintFeeRate.toString() : undefined,
          /** Ongoing annual interest rate (WAD string) — live on-chain, metadata fallback. */
          interestRate:
            m.interestRate !== null
              ? m.interestRate.toString()
              : market.interestRate,
          maxSystemDebt: market.maxSystemDebt,
          paused: !!market.paused,
          sunsetting: !!market.sunsetting,
          troveCount: m.troveCount !== null ? Number(m.troveCount) : undefined,
          addresses: {
            xapp: cfg.xapp,
            troveManager: market.troveManager,
            sortedTroves: market.sortedTroves,
            debtToken: cfg.debtToken,
            periphery: cfg.periphery ?? undefined,
          },
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}
